import Image from '@base/Image';
import styled from '@emotion/styled';
import { useIsMobile } from '@stores/AppCommonData';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import { useEffect, useState } from 'react';
import { ShowCaseContainer } from './Faculty';
import useCertificationCourseData from './useCertificationCourseData';

const Container = styled.div`
  display: grid;
  width: 100%;
  row-gap: ${Spacings.SPACING_8B};
`;
const SubContainer = styled.div`
  display: grid;
  width: 100%;
  row-gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_4B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const Title = styled(Typography)``;

const Description = styled(Typography)``;

const CompaniesContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_6B};
  position: relative;
  overflow-x: hidden;
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_3B};
    margin-top: ${Spacings.SPACING_2B};
    margin-bottom: ${Spacings.SPACING_4B};
    overflow-x: hidden;
  }
`;

const ImageWrapper = styled.div`
  width: 128px;
  height: 40px;
  position: relative;
  ${mediaQuery.uptoMobile} {
    height: 24px;
    width: 92px;
  }
`;
const CompanyLogoWrapper = styled.div`
  border-radius: ${Spacings.SPACING_2B};
  border: ${Spacings.SPACING_1} solid #dde6ed;
  padding: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_2B};
  }
  animation: horizontalScroll 10s linear 0s infinite;
  @keyframes horizontalScroll {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(-100%, 0);
    }
  }
`;

const BenefitsContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    gap: ${Spacings.SPACING_4B};
  }
`;
const BenefitContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  align-items: center;
  width: 210px;
  border-right: ${({ $isBorder = true }) => {
    return $isBorder ? '1px solid var(--color-divider-secondary)' : 'none';
  }};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  position: relative;
`;
const InfoContainer = styled.div``;

const Benefit = ({ text, icon, stats, isBorder }) => {
  return (
    <BenefitContainer $isBorder={isBorder}>
      <IconWrapper>
        <Image src={icon} layout="fill" alt={text} />
      </IconWrapper>
      <InfoContainer>
        <Title variant="h3">{stats}</Title>
        <Description variant="p2" color="var(--color-text-primary)">
          {text}
        </Description>
      </InfoContainer>
    </BenefitContainer>
  );
};

const Benefits = ({ data }) => {
  return (
    <BenefitsContainer>
      <Benefit
        text={data.salaryHikeLabel}
        icon="certificationCourses/growth-icon.svg"
        stats={data.salaryHikeAmount}
      />
      <Benefit
        text={data.jobOpportunitiesLabel}
        icon="certificationCourses/job-opportunities.svg"
        stats={data.jobOpportunitiesAmount}
      />
      <Benefit
        text={data.jobTransitionLabel}
        icon="certificationCourses/job-transitions.svg"
        stats={data.jobTransitionAmount}
        isBorder={false}
      />
    </BenefitsContainer>
  );
};

const AchieversContainer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_6B};
  flex-direction: column;
  margin-top: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_4B};
    overflow-x: hidden;
  }
`;

const AchieverList = styled.div`
  width: 768px;
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${Spacings.SPACING_6B};
`;
const AchieverContainer = styled.div`
  padding: ${Spacings.SPACING_4B};
  width: 372px;
  min-height: 342px;
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_4B};
    width: 300px;
    min-height: 336px;
  }
`;
const AchieverWrapper = styled.div`
  width: 168px;
  height: 170px;
  border-radius: ${Spacings.SPACING_2B};
  position: relative;
  img {
    border-radius: ${Spacings.SPACING_2B};
    object-fit: cover;
  }
  ${mediaQuery.uptoMobile} {
    width: 116px;
    height: 120px;
    border-radius: ${Spacings.SPACING_1B};
    img {
      border-radius: ${Spacings.SPACING_1B};
    }
  }
`;
const Name = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
`;
const Organization = styled(Typography)`
  margin-top: ${Spacings.SPACING_1B};
`;
const Declaration = styled(Typography)`
  margin-top: ${Spacings.SPACING_2B};
  max-width: 330px;
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_4B};
    white-space: break-spaces;
    min-width: 268px;
  }
`;
const TagWrapper = styled.div`
  width: 168px;
  position: relative;
  height: 45px;
  margin-top: -${Spacings.SPACING_11B};
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    width: 116px;
    height: 34px;
    margin-top: -${Spacings.SPACING_8B};
  }
`;
const Achiever = ({ achiever = {} }) => {
  const handleVideoTagClick = () => {
    window.open(achiever.achieverVideoStory, '_blank');
  };

  return (
    <AchieverContainer>
      <AchieverWrapper>
        <Image
          src={achiever.achieverProfileImage}
          layout="fill"
          alt="certificate"
        />
      </AchieverWrapper>
      {achiever?.achieverVideoStory && (
        <TagWrapper onClick={handleVideoTagClick}>
          <Image
            src="certificationCourses/watch-their-story.svg"
            layout="fill"
            alt="certificate"
          />
        </TagWrapper>
      )}
      <Name variant="h5">{achiever.achieverName}</Name>
      <Organization variant="p3" color="var(--color-text-secondary)">
        {achiever.achieverOrganisation}
      </Organization>
      <Declaration variant="p2">{achiever.achieverDescription}</Declaration>
    </AchieverContainer>
  );
};
const Achievers = ({ data = [] }) => {
  const isMobile = useIsMobile();
  const [showCarrousel, setShowCarousel] = useState(false);
  const dataLength = data.length;

  useEffect(() => {
    if (isMobile) {
      return setShowCarousel(true);
    }
    if (!isMobile && dataLength > 4) {
      return setShowCarousel(true);
    }
    return setShowCarousel(false);
  }, [isMobile, dataLength]);

  return (
    <AchieversContainer>
      {showCarrousel && (
        <ShowCaseContainer>
          <ShowCase itemsPerSlide={2} title="Meet our achiever">
            {data.map((achiever) => {
              return <Achiever key={achiever.uid} achiever={achiever} />;
            })}
          </ShowCase>
        </ShowCaseContainer>
      )}
      {!showCarrousel && <Title variant="h4">Meet our achievers</Title>}
      {!showCarrousel && (
        <AchieverList>
          {data.map((achiever) => {
            return <Achiever key={achiever.uid} achiever={achiever} />;
          })}
        </AchieverList>
      )}
    </AchieversContainer>
  );
};

const SuccessStories = ({ goalUID }) => {
  const { sections } = useCertificationCourseData(goalUID);
  const successStories = sections?.successStories ?? {};

  return (
    <Container>
      <SubContainer>
        <Title variant="h3">
          {successStories?.programmeSuccessStoriesTitle}
        </Title>
        <Description variant="p2" color="var(--color-text-primary)">
          {successStories.programmeSuceessStoriesDescription}
        </Description>
        <Benefits data={successStories} />
        <CompaniesContainer>
          {successStories?.successCompanies?.map((successCompany) => {
            return (
              <CompanyLogoWrapper key={successCompany.uid}>
                <ImageWrapper>
                  <Image
                    src={successCompany.imageSrc}
                    layout="fill"
                    alt="certificate"
                  />
                </ImageWrapper>
              </CompanyLogoWrapper>
            );
          })}
        </CompaniesContainer>
      </SubContainer>
      <Achievers data={successStories.programmeAchievers} />
    </Container>
  );
};

export default SuccessStories;
